import React from "react";
import { Callout, Code, CTA, Lead, P, Section } from "../../../../blog/components";

export const title = "How we made six coding-agent CLIs observable without wrapping their processes";
export const excerpt =
  "Claude Code, Codex, Cursor, Grok, OpenCode, and Pi all report activity differently. We turned their native hooks into one small lifecycle.";
export const heroAlt = "A wide green plain seen from a rocky hillside";
export const readingTime = "4 min read";

export default function Post() {
  return (
    <>
      <Lead>
        A terminal knows whether a process exists. It does not know whether a coding agent is working,
        waiting for approval, or done with its turn. That becomes a real problem when six of them are
        open at once.
      </Lead>

      <P>
        Cate supports Claude Code, Codex, Cursor, Grok, OpenCode, and Pi. We wanted every terminal to
        expose the same basic lifecycle without changing how any of those tools are launched:
      </P>

      <Code>{`working
  -> waiting for permission or input
  -> finished`}</Code>

      <P>
        Parsing terminal output was out. Spinners change, text gets redrawn, and a quiet process may be
        thinking, running a tool, or blocked. Watching the process tree only answers whether the CLI is
        still alive. It says nothing about the current turn.
      </P>

      <Section title="One event stream from six hook APIs">
        <P>
          Each CLI already has a structured integration point, but none of them use the same names.
          Claude Code has events such as <code>UserPromptSubmit</code>, <code>Notification</code>, and
          <code>Stop</code>. OpenCode reports <code>session.status</code>, <code>permission.asked</code>,
          and <code>session.idle</code>. Pi exposes an in-process extension API.
        </P>

        <P>Cate maps them to six internal events:</P>

        <Code>{`session-start
turn-start
permission-wait
turn-resume
turn-end
session-end`}</Code>

        <P>
          A small hook bridge sends the event to a loopback endpoint owned by the Cate runtime. The
          terminal ID travels in the hook environment, so the event reaches the right panel. Each
          terminal also gets its own token. Remote terminals use the same path, with the endpoint
          running on the remote machine.
        </P>

        <P>
          The agent process is not wrapped. Running <code>codex</code> still runs Codex directly. The
          bridge only starts when the CLI calls its native hook. If hook setup fails, Cate opens a
          normal shell and gets out of the way.
        </P>

        <Callout>
          The adapters do not pretend every CLI provides the same information. Cursor has no dedicated
          permission event, so Cate does not guess. A command that is about to run and one that is
          waiting for approval look identical through Cursor's current hook API.
        </Callout>
      </Section>

      <Section title="Turn state and process state are different">
        <P>
          A <code>turn-end</code> event means the agent is waiting for the next prompt. It does not mean
          the process has finished. Crashes and manual exits are handled separately.
        </P>

        <P>
          The first authenticated hook post gives Cate a process to track. For command hooks, the
          runtime walks up from the bridge process until it finds the matching agent. In-process
          plugins can report their PID directly. A lightweight liveness check then detects the falling
          edge and marks the terminal as finished.
        </P>

        <P>
          This is also why Cate shows no agent status when hooks never speak. Guessing from a process
          name alone would bring back the same false positives we were trying to remove.
        </P>
      </Section>

      <Section title="Session IDs need proof">
        <P>
          The hook adapters also collect the agent's session ID. Cate saves it with the terminal and
          uses the CLI's own resume command after an app restart. The obvious approach is to save the
          first ID we see. That fails for Claude Code because its first session event can arrive before
          a transcript exists. We wait for the first turn event before treating that session as
          resumable.
        </P>

        <P>
          Restore is deliberately a one-shot attempt. Cate enters the resume command into a fresh
          shell, then clears the saved stamp. A successful agent run proves itself through new hook
          events and gets stamped again. If the ID is stale, the CLI prints its normal error and the
          next restart opens a plain shell instead of retrying forever.
        </P>
      </Section>

      <Section title="Notifications follow state changes">
        <P>
          Tool-heavy turns produce a lot of events, so notifications are tied to transitions, not raw
          hook traffic. Moving from working to waiting can produce a permission notification with the
          blocked command. Repeated wait events do nothing. Clicking the notification brings the exact
          terminal forward.
        </P>

        <P>
          The public model stays small: working, waiting, finished. Most of the code lives underneath,
          where six changing hook APIs, process liveness, and resumable sessions have to agree.
        </P>
      </Section>

      <CTA label="Read the Cate source" href="https://github.com/0-AI-UG/cate" />
    </>
  );
}
